/**
 * Phase 3 — Exploration statistique différé multi-joueurs (admin).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Session } from '../../types/app';
import { BACKEND_URL } from '../../config';
import { getCardShortName } from '../../utils/cards';
import StatistiquesDiffereeSingle from './StatistiquesDiffereeSingle';
import DiffereStatsPlayTreeDrill, { type PlayTreeNode } from './DiffereStatsPlayTreeDrill';
import DiffereStatsEquivModeToggle, {
  equivModeToGroupEquiv,
  type EquivMode,
} from './DiffereStatsEquivModeToggle';
import { formatPlayTreeKeyLabel } from './differeStatsTreeLabel';
import type { ContractCode } from './StatistiquesDiffereePage.types';

function adminFetch(url: string, pseudo: string, options: RequestInit = {}) {
  const separator = url.includes('?') ? '&' : '?';
  return fetch(`${url}${separator}pseudo=${encodeURIComponent(pseudo)}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
}

interface PoolSummary {
  id: number;
  name: string | null;
  criteria: {
    minPlayers: number;
    contractTypes: ContractCode[];
    topN: number;
    minDifferes: number;
    randomSeed?: number;
  };
  differeCount: number;
  createdAt: string;
}

interface TrickDepthStats {
  depth: number;
  sampleCount: number;
  entropyRaw: number;
  entropyGrouped: number;
  top3: Array<{ key: string; card: number | null; count: number; frequency: number }>;
  distinctRaw: number;
  distinctGrouped: number;
  otherSeatsCards?: [number, number, number] | null;
  otherSeatsCardsConsistent?: boolean;
}

interface PoolDiagnostics {
  differesInPool: number;
  differesFetched: number;
  differesFetchErrors: Array<{ differeId: number; error: string }>;
  differesNoDistributions: number;
  differesNoHumanParties: number;
  humanPartiesSource: string;
  totalDistributionDeals: number;
  dealsSkippedContract: number;
  dealsSkippedNoPlays: number;
  partiesWithoutPlis: number;
  scoresSkippedNotPlayed: number;
  apiUrl: string;
}

interface ConvergenceReport {
  poolId: number;
  params: {
    topX: number;
    depth: number;
    groupEquiv: boolean;
    equivRequireOthers: boolean;
    equivRequireWinner: boolean;
    lowRankLimit: number;
  };
  diagnostics?: PoolDiagnostics;
  aggregate: {
    dealCount: number;
    byDepth: Array<{
      depth: number;
      medianEntropyRaw: number;
      meanEntropyRaw: number;
      medianEntropyGrouped: number;
      meanEntropyGrouped: number;
      pctDealsTopXUniqueCard: number;
      pctDealsTopXAtMost2Cards: number;
      pctDealsTopXAtMost3Cards: number;
      pctDealsTopXAtMost2Equiv: number;
      pctDealsTopXAtMost3Equiv: number;
    }>;
    keyMetricGsGcTopXAtMost2EquivPli1: number | null;
  };
  deals: Array<{
    differeId: number;
    dealIndex: number;
    contract: number;
    byDepth: TrickDepthStats[];
    deciles: Array<{ decile: number; label: string; byDepth: TrickDepthStats[] }>;
  }>;
  cached?: boolean;
}

interface PlayTreeReport {
  root: PlayTreeNode;
  totalSequences: number;
  cached?: boolean;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)} %`;
}

function EntropyChart({ byDepth, useGrouped }: { byDepth: ConvergenceReport['aggregate']['byDepth']; useGrouped: boolean }) {
  const maxEnt = Math.max(
    0.01,
    ...byDepth.map(d => (useGrouped ? d.meanEntropyGrouped : d.meanEntropyRaw)),
  );
  return (
    <div className="differe-stats-entropy-chart">
      {byDepth.map(d => {
        const val = useGrouped ? d.meanEntropyGrouped : d.meanEntropyRaw;
        const med = useGrouped ? d.medianEntropyGrouped : d.medianEntropyRaw;
        return (
          <div className="differe-stats-bar-group" key={d.depth}>
            <div className="differe-stats-bar-label">Pli {d.depth}</div>
            <div className="differe-stats-bar-track">
              <div
                className="differe-stats-bar differe-stats-bar--mean"
                style={{ width: `${(val / maxEnt) * 100}%` }}
                title={`Moyenne : ${val.toFixed(3)} bit`}
              />
              <div
                className="differe-stats-bar differe-stats-bar--median"
                style={{ width: `${(med / maxEnt) * 100}%` }}
                title={`Médiane : ${med.toFixed(3)} bit`}
              />
            </div>
            <span className="differe-stats-bar-value">{val.toFixed(2)}</span>
          </div>
        );
      })}
    </div>
  );
}


function equivQueryParams(
  groupEquiv: boolean,
  equivRequireOthers: boolean,
  equivRequireWinner: boolean,
  lowRankLimit: number,
): string {
  if (!groupEquiv) return 'groupEquiv=false';
  return [
    'groupEquiv=true',
    `equivRequireOthers=${equivRequireOthers ? '1' : '0'}`,
    `equivRequireWinner=${equivRequireWinner ? '1' : '0'}`,
    `lowRankLimit=${lowRankLimit}`,
  ].join('&');
}

function contractTypesQuery(types: Record<ContractCode, boolean>): string {
  return (Object.entries(types) as [ContractCode, boolean][])
    .filter(([, on]) => on)
    .map(([c]) => c)
    .join(',');
}

type ViewMode = 'pool' | 'single';

interface StatistiquesDiffereePageProps {
  session: Session;
}

export default function StatistiquesDiffereePage({ session }: StatistiquesDiffereePageProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('single');

  useEffect(() => {
    if (viewMode === 'single') {
      setTopX(20);
      setEquivMode('maxplus');
    } else {
      setTopX(30);
      setEquivMode('normal');
    }
  }, [viewMode]);

  const [pools, setPools] = useState<PoolSummary[]>([]);
  const [selectedPoolId, setSelectedPoolId] = useState<number | null>(null);
  const [loadingPools, setLoadingPools] = useState(true);
  const [creating, setCreating] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [convergence, setConvergence] = useState<ConvergenceReport | null>(null);
  const [playTree, setPlayTree] = useState<PlayTreeReport | null>(null);

  const [topX, setTopX] = useState(20);
  const [depth, setDepth] = useState(7);
  const [equivMode, setEquivMode] = useState<EquivMode>('maxplus');
  const groupEquiv = equivModeToGroupEquiv(equivMode);
  const [equivRequireOthers, setEquivRequireOthers] = useState(true);
  const [equivRequireWinner, setEquivRequireWinner] = useState(true);
  const [lowRankLimit, setLowRankLimit] = useState(5);

  const [form, setForm] = useState({
    name: '',
    minPlayers: 30,
    minDifferes: 50,
    topN: 30,
    randomSeed: '',
    contractTypes: { P: false, G: true, GS: true, GC: true } as Record<ContractCode, boolean>,
  });

  const [recentDifferes, setRecentDifferes] = useState<import('./StatistiquesDiffereeSingle').RecentDiffereItem[]>([]);
  const [recentTotal, setRecentTotal] = useState(0);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [loadingMoreRecent, setLoadingMoreRecent] = useState(false);
  const [selectedDiffereId, setSelectedDiffereId] = useState<number | null>(null);
  const [singleReport, setSingleReport] = useState<import('./StatistiquesDiffereeSingle').SingleDiffereReport | null>(null);
  const [loadingSingle, setLoadingSingle] = useState(false);
  const [benchmarkDiffereIds, setBenchmarkDiffereIds] = useState<Set<number>>(() => new Set());
  const [singleContractTypes, setSingleContractTypes] = useState<Record<ContractCode, boolean>>({
    P: false, G: true, GS: true, GC: true,
  });

  const RECENT_PAGE_SIZE = 40;
  const recentDifferesRef = useRef(recentDifferes);
  recentDifferesRef.current = recentDifferes;
  const loadingMoreRecentRef = useRef(false);

  const loadPools = useCallback(async () => {
    setLoadingPools(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/stats/differe-pool`, session.pseudo);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur chargement pools');
      setPools(data.pools ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingPools(false);
    }
  }, [session.pseudo]);

  useEffect(() => { void loadPools(); }, [loadPools]);

  const selectedPool = useMemo(
    () => pools.find(p => p.id === selectedPoolId) ?? null,
    [pools, selectedPoolId],
  );

  const loadReports = useCallback(async (poolId: number, refresh = true) => {
    setLoadingReport(true);
    setError(null);
    const q = `topX=${topX}&depth=${depth}&${equivQueryParams(groupEquiv, equivRequireOthers, equivRequireWinner, lowRankLimit)}${refresh ? '&refresh=1' : ''}`;
    try {
      const [convRes, treeRes] = await Promise.all([
        adminFetch(`${BACKEND_URL}/api/admin/stats/differe-pool/${poolId}/convergence?${q}`, session.pseudo),
        adminFetch(`${BACKEND_URL}/api/admin/stats/differe-pool/${poolId}/play-tree?${q}`, session.pseudo),
      ]);
      const conv = await convRes.json();
      const tree = await treeRes.json();
      if (!convRes.ok) throw new Error(conv.error || 'Erreur convergence');
      if (!treeRes.ok) throw new Error(tree.error || 'Erreur play-tree');
      setConvergence(conv);
      setPlayTree(tree);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingReport(false);
    }
  }, [session.pseudo, topX, depth, groupEquiv, equivRequireOthers, equivRequireWinner, lowRankLimit]);

  useEffect(() => {
    if (selectedPoolId != null && viewMode === 'pool') void loadReports(selectedPoolId);
  }, [selectedPoolId, loadReports, viewMode]);

  const loadRecentDifferes = useCallback(async (opts?: { append?: boolean }) => {
    const append = opts?.append === true;
    if (append) {
      if (loadingMoreRecentRef.current) return;
      loadingMoreRecentRef.current = true;
      setLoadingMoreRecent(true);
    } else {
      setLoadingRecent(true);
    }
    try {
      const offset = append ? recentDifferesRef.current.length : 0;
      const q = `limit=${RECENT_PAGE_SIZE}&offset=${offset}`;
      const res = await adminFetch(`${BACKEND_URL}/api/admin/stats/differe/recent?${q}`, session.pseudo);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur liste différés');
      const items = (data.items ?? []) as import('./StatistiquesDiffereeSingle').RecentDiffereItem[];
      setRecentTotal(Number(data.total ?? items.length));
      setRecentDifferes(prev => (append ? [...prev, ...items] : items));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (append) {
        loadingMoreRecentRef.current = false;
        setLoadingMoreRecent(false);
      } else {
        setLoadingRecent(false);
      }
    }
  }, [session.pseudo]);

  const loadBenchmarkPoolIds = useCallback(async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/ml/compare/differes/pool`, session.pseudo);
      const data = await res.json();
      if (!res.ok) return;
      const ids = new Set<number>();
      for (const entry of data.pool ?? []) {
        const id = Number(entry.sourceDiffereId);
        if (Number.isFinite(id) && entry.active !== false) ids.add(id);
      }
      setBenchmarkDiffereIds(ids);
    } catch {
      /* optional enrichment — ignore */
    }
  }, [session.pseudo]);

  useEffect(() => {
    if (viewMode === 'single') {
      void loadRecentDifferes();
      void loadBenchmarkPoolIds();
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps — reload only on view switch

  const loadSingleReport = useCallback(async (differeId: number, refresh = true) => {
    setLoadingSingle(true);
    setError(null);
    const ct = contractTypesQuery(singleContractTypes);
    const q = `topX=${topX}&depth=${depth}&${equivQueryParams(groupEquiv, equivRequireOthers, equivRequireWinner, lowRankLimit)}&contractTypes=${encodeURIComponent(ct)}${refresh ? '&refresh=1' : ''}`;
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/stats/differe/${differeId}/convergence?${q}`,
        session.pseudo,
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur analyse différé');
      setSingleReport(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingSingle(false);
    }
  }, [session.pseudo, topX, depth, groupEquiv, equivRequireOthers, equivRequireWinner, lowRankLimit, singleContractTypes]);

  useEffect(() => {
    if (viewMode === 'single' && selectedDiffereId != null) {
      void loadSingleReport(selectedDiffereId);
    }
  }, [viewMode, selectedDiffereId, loadSingleReport]);

  async function handleCreatePool(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError(null);
    const contractTypes = (Object.entries(form.contractTypes) as [ContractCode, boolean][])
      .filter(([, on]) => on)
      .map(([c]) => c);
    if (contractTypes.length === 0) {
      setError('Sélectionnez au moins un type de contrat.');
      setCreating(false);
      return;
    }
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/stats/differe-pool/create`, session.pseudo, {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim() || undefined,
          filters: {
            minPlayers: form.minPlayers,
            minDifferes: form.minDifferes,
            topN: form.topN,
            contractTypes,
            randomSeed: form.randomSeed ? Number(form.randomSeed) : undefined,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Création impossible');
      await loadPools();
      setSelectedPoolId(data.pool_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  }

  const pli1 = convergence?.aggregate.byDepth[0];
  const pli1Deciles = convergence?.deals[0]?.deciles;

  return (
    <div className="differe-stats-page">
      <p className="admin-section-desc">
        Analyse de convergence des ouvertures en différé multi-joueurs (top-X par donne).
        Les résultats sont recalculés à la demande ; cache serveur 24 h.
      </p>

      {error && <div className="admin-error-banner">{error}</div>}

      <div className="differe-stats-view-tabs">
        <button type="button" className={`admin-btn ${viewMode === 'single' ? 'admin-btn-primary' : ''}`}
          onClick={() => setViewMode('single')}>Différé unitaire</button>
        <button type="button" className={`admin-btn ${viewMode === 'pool' ? 'admin-btn-primary' : ''}`}
          onClick={() => setViewMode('pool')}>Pool agrégé</button>
      </div>

      <div className="differe-stats-params differe-stats-global-params">
        <label>topX <input type="number" min={5} max={viewMode === 'single' ? 2000 : 100} value={topX} onChange={e => setTopX(Number(e.target.value))} /></label>
        <label>
          depth{' '}
          <input
            type="number"
            min={1}
            max={viewMode === 'single' ? 18 : 6}
            value={depth}
            onChange={e => setDepth(Number(e.target.value))}
          />
        </label>
        {viewMode === 'single' && (
          <span className="admin-muted differe-stats-hint-inline">
            depth = tableau + liste hypothèses (1–18) · arbre = tous les plis de la donne
          </span>
        )}
        <DiffereStatsEquivModeToggle value={equivMode} onChange={setEquivMode} />
        {groupEquiv && (
          <div className="differe-stats-equiv-validation">
            <label className="differe-stats-check" title="Type A : fusion en bloc des rangs 1→N par couleur (défaut 5)">
              lowRankLimit
              <input
                type="number"
                min={5}
                max={10}
                value={lowRankLimit}
                onChange={e => setLowRankLimit(Math.min(10, Math.max(5, Number(e.target.value) || 5)))}
              />
            </label>
            <label className="differe-stats-check" title="Cartes Est/Nord/Ouest équivalentes entre replays comparés">
              <input
                type="checkbox"
                checked={equivRequireOthers}
                onChange={e => setEquivRequireOthers(e.target.checked)}
              />
              (i) Bots équivalents
            </label>
            <label className="differe-stats-check" title="Substitution carte humaine équivalente : même gagnant du pli">
              <input
                type="checkbox"
                checked={equivRequireWinner}
                onChange={e => setEquivRequireWinner(e.target.checked)}
              />
              (ii) Gagnant du pli inchangé
            </label>
          </div>
        )}
      </div>

      {viewMode === 'single' && (
        <StatistiquesDiffereeSingle
          recentDifferes={recentDifferes}
          recentTotal={recentTotal}
          loadingRecent={loadingRecent}
          loadingMoreRecent={loadingMoreRecent}
          hasMoreRecent={recentDifferes.length < recentTotal}
          onRefreshList={() => void loadRecentDifferes()}
          onLoadMore={() => void loadRecentDifferes({ append: true })}
          benchmarkDiffereIds={benchmarkDiffereIds}
          selectedDiffereId={selectedDiffereId}
          onSelectDiffere={setSelectedDiffereId}
          singleReport={singleReport}
          loadingSingle={loadingSingle}
          onRecalculate={() => selectedDiffereId != null && void loadSingleReport(selectedDiffereId, true)}
          singleContractTypes={singleContractTypes}
          setSingleContractTypes={setSingleContractTypes}
          topX={topX}
          depth={depth}
          groupEquiv={groupEquiv}
          equivMode={equivMode}
          equivRequireOthers={equivRequireOthers}
          equivRequireWinner={equivRequireWinner}
          lowRankLimit={lowRankLimit}
          contractTypes={contractTypesQuery(singleContractTypes)}
          sessionPseudo={session.pseudo}
        />
      )}

      {viewMode === 'pool' && (
      <>
      <section className="admin-card">
        <h3>Créer un pool de différés</h3>
        <form className="differe-stats-form" onSubmit={handleCreatePool}>
          <label>
            Nom (optionnel)
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </label>
          <label>
            Joueurs min.
            <input type="number" min={2} value={form.minPlayers}
              onChange={e => setForm(f => ({ ...f, minPlayers: Number(e.target.value) }))} />
          </label>
          <label>
            Nb différés
            <input type="number" min={1} value={form.minDifferes}
              onChange={e => setForm(f => ({ ...f, minDifferes: Number(e.target.value) }))} />
          </label>
          <label>
            Graine aléatoire
            <input type="number" placeholder="optionnel" value={form.randomSeed}
              onChange={e => setForm(f => ({ ...f, randomSeed: e.target.value }))} />
          </label>
          <fieldset className="differe-stats-contracts">
            <legend>Contrats (donnes retenues)</legend>
            {(['P', 'G', 'GS', 'GC'] as ContractCode[]).map(code => (
              <label key={code} className="differe-stats-check">
                <input
                  type="checkbox"
                  checked={form.contractTypes[code]}
                  onChange={e => setForm(f => ({
                    ...f,
                    contractTypes: { ...f.contractTypes, [code]: e.target.checked },
                  }))}
                />
                {code}
              </label>
            ))}
          </fieldset>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={creating}>
            {creating ? 'Création…' : 'Créer le pool'}
          </button>
        </form>
      </section>

      <section className="admin-card">
        <h3>Pools enregistrés</h3>
        {loadingPools ? (
          <p className="admin-muted">Chargement…</p>
        ) : pools.length === 0 ? (
          <p className="admin-muted">Aucun pool — créez-en un ci-dessus.</p>
        ) : (
          <ul className="differe-stats-pool-list">
            {pools.map(p => (
              <li key={p.id}>
                <button
                  type="button"
                  className={`differe-stats-pool-item ${selectedPoolId === p.id ? 'active' : ''}`}
                  onClick={() => setSelectedPoolId(p.id)}
                >
                  <strong>{p.name || `Pool #${p.id}`}</strong>
                  <span>{p.differeCount} différés · min {p.criteria.minPlayers} joueurs</span>
                  <span className="admin-muted">{new Date(p.createdAt).toLocaleString('fr-FR')}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selectedPool && (
        <section className="admin-card">
          <div className="differe-stats-toolbar">
            <h3>Analyse — {selectedPool.name || `Pool #${selectedPool.id}`}</h3>
            <div className="differe-stats-params">
              <label>topX <input type="number" min={5} max={100} value={topX} onChange={e => setTopX(Number(e.target.value))} /></label>
              <label>depth <input type="number" min={1} max={6} value={depth} onChange={e => setDepth(Number(e.target.value))} /></label>
              <DiffereStatsEquivModeToggle value={equivMode} onChange={setEquivMode} />
              <button type="button" className="admin-btn" disabled={loadingReport}
                onClick={() => void loadReports(selectedPool.id, true)}>
                Recalculer
              </button>
            </div>
          </div>

          {loadingReport && <p className="admin-muted">Calcul en cours (peut prendre plusieurs minutes)…</p>}

          {convergence?.diagnostics && convergence.aggregate.dealCount === 0 && (
            <div className="admin-card differe-stats-diagnostics">
              <h4>Aucune donne analysée — diagnostic</h4>
              <ul className="differe-stats-diag-list">
                <li>API : <code>{convergence.diagnostics.apiUrl}</code></li>
                <li>Différés chargés : {convergence.diagnostics.differesFetched} / {convergence.diagnostics.differesInPool}</li>
                <li>Source parties : <strong>{convergence.diagnostics.humanPartiesSource}</strong></li>
                <li>Donnes (distributions) : {convergence.diagnostics.totalDistributionDeals}</li>
                <li>Exclues (contrat) : {convergence.diagnostics.dealsSkippedContract}</li>
                <li>Sans plis : {convergence.diagnostics.dealsSkippedNoPlays} · parties vides : {convergence.diagnostics.partiesWithoutPlis}</li>
              </ul>
              {convergence.diagnostics.differesFetchErrors.length > 0 && (
                <ul className="differe-stats-diag-errors">
                  {convergence.diagnostics.differesFetchErrors.map(e => (
                    <li key={e.differeId}>#{e.differeId} : {e.error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {convergence && (
            <>
              <div className="admin-stats-grid differe-stats-metrics">
                <div className="admin-stat-card">
                  <span className="admin-stat-value">{convergence.aggregate.dealCount}</span>
                  <span className="admin-stat-label">Donnes analysées</span>
                </div>
                {pli1 && (
                  <>
                    <div className="admin-stat-card">
                      <span className="admin-stat-value">{pct(pli1.pctDealsTopXUniqueCard)}</span>
                      <span className="admin-stat-label">Top-{topX} : 1 carte humaine (pli 1)</span>
                    </div>
                    <div className="admin-stat-card">
                      <span className="admin-stat-value">{pct(pli1.pctDealsTopXAtMost2Cards)}</span>
                      <span className="admin-stat-label">≤ 2 cartes humaines (pli 1)</span>
                    </div>
                    <div className="admin-stat-card">
                      <span className="admin-stat-value">{pct(pli1.pctDealsTopXAtMost2Equiv)}</span>
                      <span className="admin-stat-label">≤ 2 classes équiv. (pli 1)</span>
                    </div>
                  </>
                )}
                {convergence.aggregate.keyMetricGsGcTopXAtMost2EquivPli1 != null && (
                  <div className="admin-stat-card admin-stat-card--highlight">
                    <span className="admin-stat-value">
                      {pct(convergence.aggregate.keyMetricGsGcTopXAtMost2EquivPli1)}
                    </span>
                    <span className="admin-stat-label">Mesure clé GS+GC : ≤2 équiv. pli 1</span>
                  </div>
                )}
              </div>

              <h4>Entropie moyenne par profondeur</h4>
              <div className="differe-stats-charts-row">
                <div>
                  <h5>Sans regroupement</h5>
                  <EntropyChart byDepth={convergence.aggregate.byDepth} useGrouped={false} />
                </div>
                <div>
                  <h5>Avec classes d'équivalence</h5>
                  <EntropyChart byDepth={convergence.aggregate.byDepth} useGrouped={true} />
                </div>
              </div>

              <h4>Top cartes humaines au pli 1 (échantillon : 1ère donne du rapport)</h4>
              {convergence.deals[0]?.byDepth[0]?.top3 && (
                <>
                  <table className="admin-table differe-stats-top-table">
                    <thead>
                      <tr>
                        <th>Carte / classe</th>
                        <th>Fréquence</th>
                        <th>Effectif</th>
                      </tr>
                    </thead>
                    <tbody>
                      {convergence.deals[0].byDepth[0].top3.map(row => (
                        <tr key={row.key}>
                          <td>{formatPlayTreeKeyLabel(groupEquiv ? row.key : `card:${row.card}`)}</td>
                          <td>{pct(row.frequency)}</td>
                          <td>{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!groupEquiv && convergence.deals[0].byDepth[0].otherSeatsCards && (
                    <p className="admin-muted differe-stats-hint">
                      Autres sièges (Est · Nord · Ouest) sur cette donne :{' '}
                      {convergence.deals[0].byDepth[0].otherSeatsCards
                        .map((c, i) => `${['Est', 'Nord', 'Ouest'][i]} ${getCardShortName(c as never)}`)
                        .join(' · ')}
                    </p>
                  )}
                  {!groupEquiv && convergence.deals[0].byDepth[0].otherSeatsCardsConsistent === false && (
                    <p className="admin-muted">Cartes bots divergentes entre replays sur cette donne.</p>
                  )}
                </>
              )}

              {pli1Deciles && pli1Deciles.length > 0 && (
                <>
                  <h4>Répartition par décile de classement (pli 1, 1ère donne échantillon)</h4>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Tranche</th>
                        <th>Entropie</th>
                        <th>Top carte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pli1Deciles.map(d => {
                        const t = d.byDepth[0];
                        const top = t?.top3[0];
                        return (
                          <tr key={d.decile}>
                            <td>{d.label}</td>
                            <td>{t ? t.entropyGrouped.toFixed(2) : '—'}</td>
                            <td>{top ? formatPlayTreeKeyLabel(top.key) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}

          {playTree && playTree.totalSequences > 0 && selectedPoolId != null && (
            <DiffereStatsPlayTreeDrill
              sessionPseudo={session.pseudo}
              poolId={selectedPoolId}
              topX={topX}
              depth={depth}
              groupEquiv={groupEquiv}
              equivMode={equivMode}
              equivRequireOthers={equivRequireOthers}
              equivRequireWinner={equivRequireWinner}
              lowRankLimit={lowRankLimit}
              contractTypes={contractTypesQuery(form.contractTypes)}
              playTreeRoot={playTree.root}
              totalSequences={playTree.totalSequences}
            />
          )}
        </section>
      )}
      </>
      )}
    </div>
  );
}
